import { useState } from "react";

import { queryKeys } from "@/api/hooks";
import {
  useAssetVersionQuery,
  useCreateAssetVersionMutation,
  type LibraryAsset,
  type LibraryAssetVersionDetail,
} from "@/api/library";
import { useRetryPausedQuery } from "@/api/useRetryPausedQuery";
import { useCapability } from "@/auth/SessionContext";
import {
  isPartsContent,
  isSameDraft,
  toDraft,
  toPartsContent,
  type DraftPart,
  type PartProblem,
} from "@/lib/assetContent";
import {
  classifyPublishFailure,
  editRefusal,
  type PublishFailure,
} from "@/ui/library/AssetEditor";

import type { NamedReadFailure } from "./NamedReadFailure";

/**
 * One parts-shaped asset, opened for editing as NAMED DOCUMENTS (#492).
 *
 * THE CONCURRENCY RULES ARE #33'S, UNCHANGED, AND THEY ARE THE REASON THIS IS A
 * HOOK RATHER THAN THREE COPIES. The canonical surfaces this ticket adds — the
 * main-concern row editor and the BG-set set-file editor — each edit one
 * document of an asset and publish the whole parts array, so each of them needs
 * the same four properties `AssetEditor` states:
 *
 *   * the pin is taken AT LOAD and is what Save sends as `expectedHeadVersion`,
 *     never re-read at submit time, or the check certifies nothing;
 *   * a background refetch NEVER re-seeds a DIRTY draft, so unsaved work cannot
 *     be overwritten and the collision surfaces at Save instead;
 *   * a CLEAN draft IS re-seeded when the head moved, FORWARD ONLY, so the
 *     editor cannot seed backwards onto the version a publish just replaced;
 *   * `409 stale_asset_version` keeps `AssetEditor`'s existing recovery, which
 *     is why `classifyPublishFailure` and `ConflictRecovery` are imported from
 *     there rather than reimplemented.
 *
 * WHY `AssetEditor` IS NOT REFACTORED ONTO THIS. It is reached by pathways,
 * rules, safety paths and summary through `LibraryAssetRoute`, and #492 owns
 * only main concern and BG sets. Rewriting a component four families depend on,
 * in a ticket that does not own them, is how a scoped change becomes an
 * unreviewable one. Unifying the two is a follow-up, and it is named in this
 * pull request rather than left for someone to discover.
 *
 * THE PART MANIFEST IS NOT READ HERE, deliberately. It is a separate cacheable
 * query about the SERVICE (`useLibraryPartManifestQuery`), and letting it into
 * this hook is exactly how it would come to participate in the pin.
 */

export interface NamedDraft {
  /** The head this draft was opened at, and what Save pins against. */
  pinned: string;
  /** The draft exactly as loaded — what Discard restores. */
  loaded: DraftPart[];
  draft: DraftPart[];
}

/**
 * THE FAILING ARM CARRIES THE FAILURE, not merely the fact of one.
 *
 * It used to be `{ status: "error"; retry }`, which forced both callers to
 * render one generic `ErrorState` with a Try again — so a 403 on the head read
 * became a transient outage with a control that cannot help, and a PAUSED head
 * never reached this arm at all: a held retry keeps `status: "pending"`, so it
 * fell into `loading` and stayed there. `NamedReadFailure` is the same shape the
 * BG-set deep routes carry, rendered by the same component, so all four surfaces
 * make this distinction identically (#215, #327, #4).
 */
export type NamedDraftState =
  | { status: "loading" }
  | ({ status: "error" } & NamedReadFailure)
  | { status: "unsupported"; detail: LibraryAssetVersionDetail }
  | { status: "ready"; editor: NamedDraftEditor };

export interface NamedDraftEditor {
  /** Every stored part, as the clinician currently has it. */
  parts: DraftPart[];
  /** The parts at the version this editing session loaded. */
  loaded: DraftPart[];
  /** The version this draft is pinned to. */
  pinned: string;
  dirty: boolean;
  /** Why editing is refused, or `null`. Drives every control on the surface. */
  readOnlyReason: string | null;
  readOnly: boolean;
  saving: boolean;
  /** Refusals to attribute: the client's own, then the server's. */
  problems: PartProblem[];
  failure: PublishFailure | null;
  /** The asset id, for the shared conflict recovery. */
  assetId: string;
  lastSavedAt: string | null;
  change: (parts: DraftPart[]) => void;
  discard: () => void;
  save: (options?: { onSaved?: () => void }) => void;
  /** Re-pin onto the version that refused a stale save, keeping the work. */
  rebase: (version: string) => void;
  /** Abandon this draft and edit the newer version instead. */
  takeNewer: (detail: LibraryAssetVersionDetail) => void;
}

export function useNamedDraft(asset: LibraryAsset): NamedDraftState {
  const write = useCapability("library.write");
  const head = useAssetVersionQuery(asset.id, asset.latestVersion);
  const publish = useCreateAssetVersionMutation(asset.id);
  // Cancel-then-refetch, never `head.refetch()`: the state that most needs this
  // control is the paused one, and that is the state a bare refetch cannot clear.
  const retryHead = useRetryPausedQuery(
    queryKeys.assetVersion(asset.id, asset.latestVersion),
  );

  const [session, setSession] = useState<NamedDraft | null>(null);
  const [clientProblems, setClientProblems] = useState<PartProblem[]>([]);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);

  const detail = head.data;
  const dirty = session !== null && !isSameDraft(session.draft, session.loaded);

  // Seeded during render rather than in an effect, exactly as `AssetEditor`
  // does it: the draft is derived from the loaded version, and an effect would
  // render one frame of an empty editor over content already in hand.
  if (detail && isPartsContent(detail.content)) {
    const newer =
      session !== null &&
      versionNumber(detail.version) > versionNumber(session.pinned);
    if (session === null || (newer && !dirty)) {
      const parts = toDraft(detail.content);
      setSession({ pinned: detail.version, loaded: parts, draft: parts });
      setClientProblems([]);
    }
  }

  // The blocking states apply only BEFORE there is a draft. Once a clinician has
  // one, a background head read that is pending or failing must not replace the
  // editor with a skeleton: the work is unsaved, and taking it off screen to
  // report a refetch is a worse failure than the refetch.
  if (session === null) {
    // Paused before pending, in that order: a held retry keeps
    // `status: "pending"`, so testing pending first is what put a permanent
    // skeleton over a failure the Editor Service had already answered.
    if (head.isPaused) {
      return {
        status: "error",
        // `failureReason`, never `error`: nothing has settled, so `error` is
        // still null and the failure the held retry would be retrying is here.
        failure: head.failureReason,
        isPaused: true,
        retry: retryHead,
      };
    }
    if (head.isPending) {
      return { status: "loading" };
    }
    if (head.isError) {
      return {
        status: "error",
        failure: head.error,
        isPaused: false,
        retry: retryHead,
      };
    }
  }

  if (head.data && !isPartsContent(head.data.content)) {
    return { status: "unsupported", detail: head.data };
  }

  if (session === null) {
    return { status: "loading" };
  }

  const current = session;
  const refusal = editRefusal(asset, write.reason);
  const failure = publish.isError
    ? classifyPublishFailure(publish.error, asset.latestVersion)
    : null;

  return {
    status: "ready",
    editor: {
      parts: current.draft,
      loaded: current.loaded,
      pinned: current.pinned,
      dirty,
      readOnlyReason: refusal,
      readOnly: refusal !== null,
      saving: publish.isPending,
      problems: clientProblems,
      failure,
      assetId: asset.id,
      lastSavedAt,
      change(parts) {
        setSession((held) => (held ? { ...held, draft: parts } : held));
        // Last attempt's refusals describe content that no longer exists.
        setClientProblems([]);
        publish.reset();
      },
      discard() {
        setSession((held) => (held ? { ...held, draft: held.loaded } : held));
        setClientProblems([]);
        publish.reset();
      },
      save(options) {
        const result = toPartsContent(current.draft);
        if (!result.ok) {
          setClientProblems(result.problems);
          return;
        }
        setClientProblems([]);
        publish.mutate(
          { expectedHeadVersion: current.pinned, content: result.content, note: null },
          {
            onSuccess(published) {
              // The published version IS the new baseline, re-derived from what
              // the server stored rather than from the buffers, so Discard
              // restores what actually landed.
              const parts = isPartsContent(published.content)
                ? toDraft(published.content)
                : current.draft;
              setSession({
                pinned: published.version,
                loaded: parts,
                draft: parts,
              });
              setLastSavedAt(published.createdAt);
              options?.onSaved?.();
            },
          },
        );
      },
      rebase(version) {
        setSession((held) => (held ? { ...held, pinned: version } : held));
        publish.reset();
      },
      takeNewer(newer) {
        const parts = isPartsContent(newer.content) ? toDraft(newer.content) : [];
        setSession({ pinned: newer.version, loaded: parts, draft: parts });
        setClientProblems([]);
        publish.reset();
      },
    },
  };
}

/**
 * `v7` -> 7. Only ever compared, never used to derive the next label — the
 * contract says so explicitly, and the server allocates under a row lock.
 */
function versionNumber(label: string): number {
  return Number.parseInt(label.slice(1), 10);
}
